import React from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  ApiDownloadButton,
  EmptyIndicator,
  LoadingIndicator,
  Pagination
} from 'components/common';
import { Box } from 'components/common/box';
import { FiltersProvider } from 'components/common/filter';
import { CompletionHistorySubHeader, CompletionHistoryTable } from '.';

import type { GetCompletionsRes } from 'shared-types/responses';
import type { FiltersProviderProps } from 'components/common/filter/FilterContext';

interface CompletionHistoryProps {
  completionsType: 'all' | 'team';
  filters: FiltersProviderProps['filters'];
  infoText?: string;
}

const CompletionHistory: React.FC<CompletionHistoryProps> = ({
  completionsType,
  filters,
  infoText
}) => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const completionsPerPage = 10;

  const apiUrl =
    completionsType === 'team'
      ? `/admin/supervisors/${user?.id}/team-completions`
      : '/admin/analytics/completions';

  const queryParams = Object.keys(router?.query || {})
    .map((key) => `&${key}=${router.query[key]}`)
    .join('');

  const { data, loading, setLoading } = useApiData<GetCompletionsRes>({
    url: `${apiUrl}?limit=${completionsPerPage}${queryParams}`
  });

  return (
    <div className="completions">
      <FiltersProvider
        filters={filters}
        onBeforeSubmitOrClear={() => setLoading(true)}
      >
        <Box
          headerContent={
            <ApiDownloadButton
              category="success"
              filename={`${user?.email || user?.username}.xlsx`}
              icon="file-excel"
              size="header"
              style={{ whiteSpace: 'nowrap' }}
              text={messages.usersDownloadSummary}
              url={`${apiUrl}?exportToExcel=true${queryParams}`}
            />
          }
          headerContentWrap={true}
          padding={0}
          subHeaderContent={
            <CompletionHistorySubHeader setLoading={setLoading} />
          }
          title={messages.profileCompletionHistory}
          titleCount={data?.numberOfCompletions || 0}
          titleTooltip={infoText}
        >
          {loading ? (
            <LoadingIndicator />
          ) : data?.completions?.length ? (
            <CompletionHistoryTable completions={data.completions} />
          ) : (
            <EmptyIndicator icon="list" title={messages.commonNoData} />
          )}
        </Box>

        <Pagination
          currentPage={Number(router.query?.page || 1)}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => {
            router.replace({ query: { ...router.query, page } });
          }}
        />
      </FiltersProvider>
    </div>
  );
};

export default CompletionHistory;
